// --- React & Redux
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
// --- External UI Libraries
import { Row } from 'antd';
import Input from '@dtci/spellbook/Input';
import Select from '@dtci/spellbook/Select';
// --- API & Utils
import { datastageApi } from 'datastage/api';
import { getStaticInputVisibilityFlags } from '../utils/validations';
// --- Hooks & Store
import { useRightsActions } from 'datastage/store/rights/useRights';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel/InputLabel';
// --- Styles
import '../../styles.scss';

const DEBOUNCE_TIMEOUT = 500;

/**
 * Static Search Component
 *
 * Features:
 * - Displays static input fields for Deal and Catalog based on visibility flags
 * - Retrieves values from the association form in Redux state
 * - When deal/catalog is not found (404), shows a search input with error message
 *
 * @param details
 * @returns {Element}
 * @constructor
 */
export function StaticSearch({ details }) {
    const timeoutRef = useRef();
    const { associationForm } = useSelector((state) => state.rights);
    const { updateRightsForm } = useRightsActions();
    const { shouldShowDealStaticInput, shouldShowCatalogStaticInput } =
        getStaticInputVisibilityFlags(details);

    // Handle deal search input changes with debouncing (used when deal not found)
    const onDealOptionChange = (input, loadOptions) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(async () => {
            if (input !== '') {
                const data = await datastageApi.deals(input);
                const mappedOptions = Array.isArray(data) ? data.map(item => ({
                    id: item?.id,
                    label: item?.searchableTitle || `${item?.id} - ${item?.title}`
                })) : [];
                loadOptions(mappedOptions);
            }
        }, DEBOUNCE_TIMEOUT);
    };

    // Handle catalog search input changes with debouncing (used when catalog not found)
    const onCatalogOptionChange = (input, loadOptions) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(async () => {
            if (input !== '') {
                const data = await datastageApi.catalogs(input);
                const mappedOptions = Array.isArray(data) ? data.map(item => ({
                    id: item?.id,
                    label: item?.searchableTitle || `${item?.id} - ${item?.title}`
                })) : [];
                loadOptions(mappedOptions);
            }
        }, DEBOUNCE_TIMEOUT);
    };

    const handleDealChange = (value) => {
        updateRightsForm({
            key: 'associationForm',
            data: { dealSelected: value },
        });
    };

    const handleCatalogChange = (value) => {
        updateRightsForm({
            key: 'associationForm',
            data: { catalogSelected: value },
        });
    };

    // Get the ID that was searched for (to show in error message)
    const getSearchedDealId = () => {
        if (details?.failedRelationship?.parentRightsLineId != null && 
            details?.failedRelationship?.parentEntityType === 'DEAL') {
            return details.failedRelationship.parentRightsLineId;
        }
        return details?.dealId > -1 ? details.dealId : 
               (details?.associationEntity?.dealId > -1 ? details.associationEntity.dealId : null);
    };

    const getSearchedCatalogId = () => {
        if (details?.failedRelationship?.parentRightsLineId != null && 
            details?.failedRelationship?.parentEntityType === 'CATALOG_ITEM') {
            return details.failedRelationship.parentRightsLineId;
        }
        return details?.catalogId > -1 ? details.catalogId : 
               (details?.associationEntity?.catalogId > -1 ? details.associationEntity.catalogId : null);
    };

    return (
        <>
            {shouldShowDealStaticInput && (
                <Row>
                    {associationForm?.dealNotFound ? (
                        // Deal not found - show error message and search input
                        <InputLabel colProps={{ span: 24 }} label="Deal - Search by ID or Title *">
                            <div className="not-found-error">
                                The deal {getSearchedDealId()} on this failed relationship was not found, please select another deal...
                            </div>
                            <Select
                                async
                                className="search-input"
                                loadOptions={onDealOptionChange}
                                isClearable
                                isSearchable
                                onChange={handleDealChange}
                                value={associationForm?.dealSelected}
                                placeholder="Search for a deal..."
                            />
                        </InputLabel>
                    ) : (
                        // Deal found - show static input
                        <InputLabel colProps={{ span: 24 }} label="Deal">
                            <Input
                                value={
                                    associationForm?.deal && associationForm?.deal.id && associationForm?.deal.title
                                        ? `${associationForm.deal.id} - ${associationForm.deal.title}`
                                        : ''
                                }
                                width="auto"
                                id={details.dealId}
                                name={details.dealId}
                                isDisabled
                            />
                        </InputLabel>
                    )}
                </Row>
            )}

            {shouldShowCatalogStaticInput && (
                <Row>
                    {associationForm?.catalogNotFound ? (
                        // Catalog not found - show error message and search input
                        <InputLabel colProps={{ span: 24 }} label="Catalog - Search by ID or Title *">
                            <div className="not-found-error">
                                The catalog {getSearchedCatalogId()} on this failed relationship was not found, please select another catalog...
                            </div>
                            <Select
                                async
                                className="search-input"
                                loadOptions={onCatalogOptionChange}
                                isClearable
                                isSearchable
                                onChange={handleCatalogChange}
                                value={associationForm?.catalogSelected}
                                placeholder="Search for a catalog..."
                            />
                        </InputLabel>
                    ) : (
                        // Catalog found - show static input
                        <InputLabel colProps={{ span: 24 }} label="Catalog">
                            <Input
                                value={
                                    associationForm?.catalog && associationForm?.catalog.id && associationForm?.catalog.title
                                        ? `${associationForm?.catalog.id} - ${associationForm?.catalog.title}`
                                        : ''
                                }
                                width="auto"
                                id={details.catalogId}
                                name={details.catalogId}
                                isDisabled
                            />
                        </InputLabel>
                    )}
                </Row>
            )}
        </>
    );
}
